import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'sonner'
import * as yup from 'yup'

import { LogoIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useAppDispatch } from '@/hooks'
import authService from '@/services/auth-service'
import { login } from '@/slices/auth'
import { Loader2 } from 'lucide-react'

export const ForgotPassword = () => {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const schema = yup.object<{ email: string }>().shape({
		email: yup
			.string()
			.required('E-Mail ist erforderlich!')
			.email('Geben Sie eine gültige E-Mail-Adresse ein'),
	})

	const form = useForm<{ email: string }>({
		resolver: yupResolver(schema),
		defaultValues: { email: '' },
		mode: 'all',
	})

	const { mutate, isLoading } = useMutation({
		mutationKey: 'forgot-password',
		mutationFn: authService.forgotPassword,
	})

	const onSubmit = (data: { email: string }) => {
		mutate(data, {
			onSuccess: data => {
				toast.success(data?.message)
				dispatch(login(data.data))
				navigate('/login')
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
			},
		})
	}

	return (
		<div className="bg-[#F7F8F8] overflow-auto h-screen">
			<div className="lg:container max-lg:mx-5 pt-10 lg:pt-[77px] mx-auto w-11/12 lg:min-w-[751px] lg:w-[45%] flex flex-col items-start">
				<div className="flex items-center gap-[7px]">
					<LogoIcon className="max-lg:size-14" />
					<div className="flex flex-col gap-2 lg:gap-3">
						<h2 className="text-secondary lg:text-xl font-extrabold">minmini</h2>
						<p className="max-lg:text-xs max-lg:w-2/3 text-muted-foreground">
							(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.
						</p>
					</div>
				</div>
				<div className="bg-white mt-1.5 lg:mt-3 rounded-3xl lg:rounded-[32px] pt-5 lg:pt-10 pb-[18px] lg:pb-5 px-4 lg:px-10 flex flex-col">
					<h1 className="text-secondary font-bold lg:text-2xl">Passwort vergessen!</h1>
					<p className="mt-2 leading-[22px] lg:leading-8 max-lg:text-xs text-muted-foreground font-medium">
						Passwort vergessen? Gib hier deine E-Mail-Adresse ein. Anschließend erhältst du einen
						Link per E-Mail, um ein neues Passwort zu erstellen.
					</p>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col mt-6 lg:mt-12">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>E-Mail</FormLabel>
										<FormControl>
											<Input {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								disabled={isLoading}
								className="rounded-lg mt-[168px] lg:mt-[190px] lg:text-xl font-medium"
								variant="secondary">
								{isLoading && <Loader2 className="animate-spin" />}
								{isLoading ? 'Bitte warten' : 'E-Mail senden'}
							</Button>
							<p className="mt-3 lg:mt-5 text-center text-muted-foreground text-[10px] lg:text-sm font-medium">
								Du hast noch keinen Zugang?{' '}
								<span className="text-secondary font-extrabold">Buche jetzt dein Regal</span>
							</p>
						</form>
					</Form>
				</div>
			</div>
		</div>
	)
}
